import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Radio, Video, Info } from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./settings.scss";

/**
 * MeetingProviderSelector
 *
 * Top-level switch that controls which meeting provider an institute uses
 * for live classes. New classes use the chosen provider; in-flight classes
 * continue on their original provider (snapshotted at start time).
 */
const PROVIDER_LABELS = { zoom: "Zoom", zoho: "Zoho Meeting" };

const MeetingProviderSelector = () => {
  const [provider, setProvider]           = useState("zoom");
  const [zohoConnected, setZohoConnected] = useState(false);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [toast, setToast]                 = useState({ show: false, message: "", type: "success" });
  const showToast = (message, type = "success") => setToast({ show: true, message, type });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/institute/zoho-meeting-settings");
      setProvider(data.meetingProvider || "zoom");
      setZohoConnected(!!data.connected);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to load provider settings.", "error");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const handleChange = async (next) => {
    if (next === provider) return;
    if (next === "zoho" && !zohoConnected) {
      showToast("Connect your Zoho Meeting account first.", "error");
      return;
    }
    setSaving(true);
    try {
      await apiClient.put("/institute/meeting-provider", { provider: next });
      setProvider(next);
      showToast(`Live class provider set to ${PROVIDER_LABELS[next]}.`);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to switch provider.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner size="sm" /> Loading provider…
      </div>
    );
  }

  const renderProviderCard = (key, icon, title, description, disabled) => {
    const active = provider === key;
    return (
      <div
        key={key}
        className={`zoom-mode-card ${active ? "zoom-mode-card--active" : ""}`}
        onClick={() => { if (!disabled && !saving) handleChange(key); }}
        style={{ opacity: disabled ? 0.6 : 1, cursor: disabled || saving ? "not-allowed" : "pointer" }}
      >
        <div className="zoom-mode-card__header">
          <input
            type="radio"
            name="meeting-provider"
            checked={active}
            readOnly
            disabled={disabled || saving}
          />
          {icon}
          <strong>{title}</strong>
        </div>
        <p className="zoom-mode-card__desc">{description}</p>
        {disabled && (
          <p className="zoom-mode-card__desc" style={{ color: "var(--warning, #d97706)", marginTop: 4 }}>
            <Info size={12} className="me-1" />
            Connect a Zoho Meeting account below to enable.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="zoom-settings">
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Radio size={18} /> Live Class Provider
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">
            Choose which video platform new live classes will use. In-flight classes continue on their original provider.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="zoom-mode-selector">
            {renderProviderCard(
              "zoom",
              <Video size={16} />,
              "Zoom",
              "Default — full webhook integration, embedded host SDK, platform-hosted pool support.",
              false
            )}
            {renderProviderCard(
              "zoho",
              <Video size={16} />,
              "Zoho Meeting",
              "Alternative — uses your institute's own Zoho account. Recordings via polling, instructor joins in new tab.",
              !zohoConnected
            )}
          </div>
        </AppCardContent>
      </AppCard>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default MeetingProviderSelector;
